// ─────────────────────────────────────────────────────────────────────────────
// VendorCostComparisonPanel
//
// Shown while editing/creating a material request. For every line item it
// looks at that item's known vendor prices (vendorPrices[], already loaded
// onto each item row) plus the item's default vendor, and answers:
//
//   "What would this order cost if every line used its DEFAULT vendor,
//    vs. what would it cost if every line used the CHEAPEST vendor,
//    vs. what am I actually paying right now?"
//
// This lets you see in real time whether sticking with a default/in-stock
// vendor is costing you money, or whether chasing the cheapest price per
// item would save meaningfully — before you save/generate the order.
//
// Props:
//   items    — the RequestForm items array (each has vendor, vendorPrices,
//              isCoverage, coveragePerUnit, quantity, unitPrice, ratePerUnit,
//              itemDatabaseId, itemCode, defaultVendorId (optional))
//   vendors  — full vendor list [{ _id, companyName }]
//   priceMap — project order-history price map, same shape as passed into
//              RequestForm: { [itemCode]: { [vendorId]: { unitPrice, ... } } }
//   onApplyVendorPlan(nextVendorByIdx) — called with { [itemIdx]: vendorId }
//              when the user clicks "Apply" on a plan. RequestForm should
//              run this through its existing per-row updateItem("vendor", …)
//              so all the normal price-resolution/vendorPriceStatus logic
//              still applies.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from "react";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const cls = (...c) => c.filter(Boolean).join(" ");

const calcPackageCost = (ratePerUnit, coverage) =>
  parseFloat(ratePerUnit || 0) * (parseFloat(coverage) || 1);

// Resolve every vendor option's package cost ($/PC) for a single item row,
// using the same priority order as the main form: project order history →
// item database vendor price. (Budget default cost is deliberately excluded
// here — we only want vendors we could actually order from.)
function vendorOptionsForItem(item, priceMap) {
  const itemCode = (item.itemCode || "").trim();
  const histForCode = priceMap?.[itemCode] || {};
  const options = new Map(); // vendorId -> { packageCost, source }

  (item.vendorPrices || []).forEach((vp) => {
    const vid = (vp.vendor?._id || vp.vendor)?.toString();
    if (!vid) return;
    const rate = vp.price || vp.unitRate || 0;
    const packageCost = item.isCoverage
      ? calcPackageCost(rate, item.coveragePerUnit)
      : rate;
    options.set(vid, { packageCost, source: "item database" });
  });

  Object.entries(histForCode).forEach(([vid, hist]) => {
    if (!vid || vid === "__no_vendor__") return;
    options.set(vid, {
      packageCost: hist.unitPrice || 0,
      source: `MO #${hist.orderNumber}`,
    });
  });

  return options; // Map<vendorId, { packageCost, source }>
}

export default function VendorCostComparisonPanel({
  items,
  vendors,
  priceMap,
  onApplyVendorPlan,
}) {
  const [expanded, setExpanded] = useState(false);

  const analysis = useMemo(() => {
    let currentTotal = 0;
    let cheapestTotal = 0;
    let defaultTotal = 0;
    let defaultCoverageCount = 0; // # items where a default-vendor price was actually found

    const rows = items.map((item) => {
      const qty = parseFloat(item.quantity) || 0;
      const currentCost = parseFloat(item.unitPrice) || 0;
      currentTotal += currentCost * qty;

      const options = vendorOptionsForItem(item, priceMap);

      // Cheapest available vendor for this line
      let cheapest = null;
      options.forEach((opt, vid) => {
        if (!cheapest || opt.packageCost < cheapest.packageCost) {
          cheapest = { vendorId: vid, ...opt };
        }
      });
      // If nothing known, fall back to current price/vendor so totals stay sane
      if (!cheapest) {
        cheapest = {
          vendorId: item.vendor || null,
          packageCost: currentCost,
          source: "current",
        };
      }
      cheapestTotal += cheapest.packageCost * qty;

      // Default vendor for this line (item.defaultVendorId set by parent,
      // falls back to whatever vendor is currently selected if none known)
      const defaultVendorId = item.defaultVendorId || null;
      let defaultOpt = defaultVendorId ? options.get(defaultVendorId) : null;
      if (!defaultOpt) {
        defaultOpt = {
          packageCost: currentCost,
          source: defaultVendorId ? "no price on file" : "no default set",
        };
      } else {
        defaultCoverageCount += 1;
      }
      defaultTotal += defaultOpt.packageCost * qty;

      return {
        item,
        qty,
        currentCost,
        cheapest,
        defaultVendorId,
        defaultCost: defaultOpt.packageCost,
        defaultSource: defaultOpt.source,
        vendorCount: options.size,
      };
    });

    return {
      rows,
      currentTotal,
      cheapestTotal,
      defaultTotal,
      defaultCoverageCount,
    };
  }, [items, priceMap]);

  if (!items.length) return null;

  const savingsVsCheapest = analysis.currentTotal - analysis.cheapestTotal;
  const deltaVsDefault = analysis.currentTotal - analysis.defaultTotal;

  const applyCheapest = () => {
    const plan = {};
    analysis.rows.forEach((r, idx) => {
      if (r.cheapest.vendorId) plan[idx] = r.cheapest.vendorId;
    });
    onApplyVendorPlan(plan);
  };

  const applyDefault = () => {
    const plan = {};
    analysis.rows.forEach((r, idx) => {
      if (r.defaultVendorId) plan[idx] = r.defaultVendorId;
    });
    onApplyVendorPlan(plan);
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h4 className="text-sm font-semibold text-white">
            Vendor Cost Comparison
          </h4>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Live comparison across {items.length} line
            {items.length !== 1 ? "s" : ""} — see what switching vendors would
            cost you.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="text-[11px] text-orange-400 hover:text-orange-300 underline"
        >
          {expanded ? "Hide per-item breakdown" : "Show per-item breakdown"}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Current selection */}
        <div className="bg-gray-700/60 rounded-lg border border-gray-600 p-3">
          <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">
            Current Selection
          </p>
          <p className="text-lg font-bold font-mono text-gray-100">
            ${fmt(analysis.currentTotal)}
          </p>
          <p className="text-[10px] text-gray-500 mt-0.5">
            What you're paying right now
          </p>
        </div>

        {/* Cheapest per item */}
        <div className="bg-emerald-950/30 rounded-lg border border-emerald-700/40 p-3">
          <p className="text-[10px] text-emerald-400 uppercase tracking-wide mb-1">
            Cheapest per Item
          </p>
          <p className="text-lg font-bold font-mono text-emerald-300">
            ${fmt(analysis.cheapestTotal)}
          </p>
          <p
            className={cls(
              "text-[10px] mt-0.5 font-medium",
              savingsVsCheapest > 0.005
                ? "text-emerald-400"
                : savingsVsCheapest < -0.005
                  ? "text-red-400"
                  : "text-gray-500",
            )}
          >
            {savingsVsCheapest > 0.005
              ? `Save $${fmt(savingsVsCheapest)} vs current`
              : savingsVsCheapest < -0.005
                ? `$${fmt(Math.abs(savingsVsCheapest))} more than current`
                : "Same as current"}
          </p>
          <button
            type="button"
            onClick={applyCheapest}
            className="mt-2 w-full px-2 py-1 text-[10px] font-semibold bg-emerald-700 hover:bg-emerald-600 text-white rounded transition-colors"
          >
            Apply cheapest to all
          </button>
        </div>

        {/* Default vendor */}
        <div className="bg-blue-950/30 rounded-lg border border-blue-700/40 p-3">
          <p className="text-[10px] text-blue-400 uppercase tracking-wide mb-1">
            Default Vendor{" "}
            <span className="text-gray-500 normal-case">
              ({analysis.defaultCoverageCount}/{items.length} priced)
            </span>
          </p>
          <p className="text-lg font-bold font-mono text-blue-300">
            ${fmt(analysis.defaultTotal)}
          </p>
          <p
            className={cls(
              "text-[10px] mt-0.5 font-medium",
              deltaVsDefault > 0.005
                ? "text-emerald-400"
                : deltaVsDefault < -0.005
                  ? "text-orange-400"
                  : "text-gray-500",
            )}
          >
            {deltaVsDefault > 0.005
              ? `$${fmt(deltaVsDefault)} cheaper than current`
              : deltaVsDefault < -0.005
                ? `$${fmt(Math.abs(deltaVsDefault))} more than current — but in-stock/fast`
                : "Same as current"}
          </p>
          <button
            type="button"
            onClick={applyDefault}
            className="mt-2 w-full px-2 py-1 text-[10px] font-semibold bg-blue-700 hover:bg-blue-600 text-white rounded transition-colors"
          >
            Apply default to all
          </button>
        </div>
      </div>

      {expanded && (
        <div className="overflow-x-auto rounded-lg border border-gray-700 mt-1">
          <table className="w-full text-xs">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-3 py-2 text-left text-gray-400">Item</th>
                <th className="px-3 py-2 text-right text-gray-400">Qty</th>
                <th className="px-3 py-2 text-right text-gray-400">
                  Current $/PC
                </th>
                <th className="px-3 py-2 text-right text-gray-400">
                  Cheapest $/PC
                </th>
                <th className="px-3 py-2 text-left text-gray-400">
                  Cheapest Vendor
                </th>
                <th className="px-3 py-2 text-right text-gray-400">
                  Default $/PC
                </th>
                <th className="px-3 py-2 text-center text-gray-400">
                  # Vendors
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {analysis.rows.map((r, i) => {
                const cheapestVendor = vendors.find(
                  (v) => v._id?.toString() === r.cheapest.vendorId,
                );
                const isCurrentCheapest =
                  r.cheapest.vendorId &&
                  r.item.vendor &&
                  r.cheapest.vendorId.toString() === r.item.vendor.toString();
                return (
                  <tr
                    key={i}
                    className={
                      i % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20"
                    }
                  >
                    <td className="px-3 py-2 text-gray-200">
                      <p className="font-medium">{r.item.description}</p>
                      <p className="text-[10px] text-orange-400 font-mono">
                        {r.item.itemCode}
                        {r.item.size ? ` · ${r.item.size}` : ""}
                      </p>
                    </td>
                    <td className="px-3 py-2 text-right text-gray-300 font-mono">
                      {r.qty}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-300 font-mono">
                      ${fmt(r.currentCost)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-300">
                      ${fmt(r.cheapest.packageCost)}
                    </td>
                    <td className="px-3 py-2 text-left">
                      <span
                        className={cls(
                          "text-[10px] px-1.5 py-0.5 rounded font-medium",
                          isCurrentCheapest
                            ? "bg-gray-700/50 text-gray-400"
                            : "bg-emerald-900/50 text-emerald-300 border border-emerald-700/40",
                        )}
                      >
                        {cheapestVendor?.companyName || "—"}
                        {isCurrentCheapest ? " (current)" : ""}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-blue-300">
                      {r.defaultVendorId ? (
                        `$${fmt(r.defaultCost)}`
                      ) : (
                        <span className="text-gray-600">no default</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center text-gray-400">
                      {r.vendorCount}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
